// iter622 — Unified "Pending advances + Pending fees" inbox.
//
// One place to see and clear:
//   - Pending bank-fee bookings (statement lines we haven't yet posted)
//   - Pending pass-through advances (money out, still unaccounted)

import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Inbox, ChevronRight, Wallet, Receipt, AlertTriangle, Clock, Sparkles, Repeat, Pause, Play, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cur } from '@/lib/currency';
import { Link } from 'react-router-dom';

export default function ReconInboxPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(60);
  // iter623 — AI Inbox Clearing (week / month / quarter / year).
  const [aiResult, setAiResult] = useState(null);
  const [aiBusy, setAiBusy] = useState(null);
  // iter624 — Track which suggestions the user has applied so the
  // button switches to "✓ Done" and disables.
  const [appliedIds, setAppliedIds] = useState(new Set());
  // iter624 — Scheduled clearing subscriptions.
  const [subs, setSubs] = useState({});
  const loadSubs = async () => {
    try {
      const { data } = await api.get('/recon-inbox/schedule');
      const m = {};
      (data.subscriptions || []).forEach(s => { m[s.period] = s; });
      setSubs(m);
    } catch { /* */ }
  };
  const toggleSub = async (period) => {
    const enabled = !subs[period]?.enabled;
    try {
      await api.post('/recon-inbox/schedule', { period, enabled });
      toast.success(enabled ? `Scheduled ${period}ly AI clearing — emails coming.` : `Unsubscribed from ${period}ly AI clearing.`);
      loadSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle subscription');
    }
  };
  const applySuggestion = async (s, idx, kind) => {
    try {
      await api.post('/recon-inbox/apply-suggestion', s);
      setAppliedIds(prev => new Set([...prev, `${kind}-${idx}`]));
      toast.success(kind === 'fee' ? 'Fee booked.' : 'Chase logged.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply suggestion');
    }
  };

  // iter625 — Bulk "Do all suggestions".
  const [bulkBusy, setBulkBusy] = useState(false);
  const doAll = async () => {
    if (!aiResult) return;
    const items = [
      ...(aiResult.fee_suggestions || []),
      ...(aiResult.advance_suggestions || []).map(a => ({ action: 'chase', advance_id: a.id })),
    ];
    if (items.length === 0) return;
    if (!window.confirm(`Apply ${items.length} suggestion(s) in bulk? This will post bank-fee bookings + chase logs.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/recon-inbox/bulk-apply', { suggestions: items });
      toast.success(`Applied ${data.applied}, failed ${data.failed}, skipped ${data.skipped}.`);
      // Mark every row as done.
      const all = new Set();
      (aiResult.fee_suggestions || []).forEach((_, i) => all.add(`fee-${i}`));
      (aiResult.advance_suggestions || []).forEach((_, i) => all.add(`advance-${i}`));
      setAppliedIds(all);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk apply failed');
    } finally { setBulkBusy(false); }
  };

  // iter625 — Dry-run preview (peek at the trial-balance impact).
  const [dryRun, setDryRun] = useState(null);
  const previewSuggestion = async (s) => {
    try {
      const { data } = await api.post('/recon-inbox/dry-run', s);
      setDryRun({ ...data, suggestion: s });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry-run failed');
    }
  };
  const runAiClearing = async (period) => {
    try {
      setAiBusy(period);
      const { data } = await api.post('/recon-inbox/ai-clear', { period });
      setAiResult({ period, ...data });
      toast.success(`AI plan ready for the ${period}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI clearing failed');
    } finally { setAiBusy(null); }
  };

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/recon-inbox/pending', { params: { days } });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  useEffect(() => { loadSubs(); loadRecurring(); }, []);

  // iter626 — Auto-book recurring fee rules (option b: dry-run + 1-click).
  const [rrules, setRrules] = useState({ rules: [], matches: [], candidates: [] });
  const [rrBusy, setRrBusy] = useState(false);
  const loadRecurring = async () => {
    try {
      const [rules, matches] = await Promise.all([
        api.get('/recon-inbox/recurring-rules'),
        api.get('/recon-inbox/recurring-rules/matches', { params: { days: 60 } }),
      ]);
      setRrules(prev => ({ ...prev, rules: rules.data.rules || [], matches: matches.data.matches || [] }));
    } catch { /* */ }
  };
  const scanRecurring = async () => {
    setRrBusy(true);
    try {
      const { data } = await api.get('/recon-inbox/recurring-rules/scan', { params: { months: 3 } });
      setRrules(prev => ({ ...prev, candidates: data.candidates || [] }));
      if ((data.candidates || []).length === 0) {
        toast.info('No recurring fee patterns found in the last 3 months.');
      } else {
        toast.success(`Found ${data.candidates.length} recurring candidate(s).`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally { setRrBusy(false); }
  };
  const activateRule = async (c) => {
    try {
      await api.post('/recon-inbox/recurring-rules', {
        category: c.category,
        mean_amount: c.mean_amount,
        tolerance_pct: Math.max(10, Math.ceil(c.tolerance_pct || 10)),
      });
      toast.success(`Rule activated for ${c.category.replace(/_/g, ' ')}.`);
      loadRecurring();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Activation failed');
    }
  };
  const updateRuleStatus = async (rule, status) => {
    try {
      await api.patch(`/recon-inbox/recurring-rules/${rule.id}`, { status });
      toast.success(`Rule ${status}.`);
      loadRecurring();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };
  const applyRuleMatch = async (m) => {
    try {
      await api.post('/recon-inbox/apply-suggestion', {
        action: 'book_expense',
        category: m.category,
        period: m.period,
        amount: m.amount,
      });
      toast.success(`Booked ${m.category.replace(/_/g, ' ')} for ${m.period}.`);
      loadRecurring();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    }
  };

  const sym = (a) => `${cur()} ${Number(a || 0).toLocaleString()}`;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="recon-inbox">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight flex items-center gap-2">
              <Inbox size={24} className="text-indigo-600" />
              Pending Inbox
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Stale items that need clearing — bank fees waiting to be booked + pass-through advances with unaccounted balance.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="text-[12px] rounded-lg border border-border px-2 py-1.5 bg-white"
              data-testid="inbox-days-filter"
            >
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
            <Button onClick={load} variant="outline" className="rounded-full" data-testid="inbox-refresh">
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading…
          </div>
        ) : data ? (
          <>
            {/* Topline tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Card className="border-rose-200 bg-rose-50/40">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase font-bold tracking-wider text-rose-700">Pending fees</div>
                      <div className="text-2xl font-bold mt-0.5">{sym(data.totals.fees_amount)}</div>
                      <div className="text-[12px] text-rose-600/80">{data.totals.fees_count} bucket(s) need booking</div>
                    </div>
                    <Receipt size={32} className="text-rose-300" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50/40">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase font-bold tracking-wider text-amber-700">Open advances</div>
                      <div className="text-2xl font-bold mt-0.5">{sym(data.totals.advances_amount)}</div>
                      <div className="text-[12px] text-amber-600/80">{data.totals.advances_count} person(s) with unaccounted balance</div>
                    </div>
                    <Wallet size={32} className="text-amber-300" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Pending fees list */}
            <Card data-testid="inbox-fees-section">
              <CardContent className="p-4">
                <h2 className="font-bold font-outfit text-base flex items-center gap-2 mb-3">
                  <Receipt size={16} className="text-rose-600" /> Pending bank-fee bookings
                </h2>
                {data.pending_fees.length === 0 ? (
                  <div className="text-[13px] text-muted-foreground italic py-4 text-center">
                    🎉 No pending fees in the last {days} days. All bank charges have been posted.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {data.pending_fees.map((f, idx) => (
                      <div key={`${f.category}-${f.period}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 border border-border/40" data-testid={`inbox-fee-${idx}`}>
                        <Badge className="bg-rose-100 text-rose-800 border-rose-300 border">{f.category.replace(/_/g, ' ')}</Badge>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold">{f.period} · {f.count} txn(s)</div>
                          <div className="text-[11px] text-muted-foreground">latest {f.latest_date}{f.age_days != null && f.age_days > 30 && <span className="text-rose-700 ml-2"><Clock size={10} className="inline" /> {f.age_days}d old</span>}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-rose-700">{sym(f.amount)}</div>
                        </div>
                        <Link to={`/bank-statements/${f.statement_ids[0]}`} className="text-[11px] text-indigo-600 hover:underline flex items-center gap-0.5">
                          Book it <ChevronRight size={11} />
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending advances list */}
            <Card data-testid="inbox-advances-section">
              <CardContent className="p-4">
                <h2 className="font-bold font-outfit text-base flex items-center gap-2 mb-3">
                  <Wallet size={16} className="text-amber-600" /> Pending advances (unaccounted balance)
                </h2>
                {data.pending_advances.length === 0 ? (
                  <div className="text-[13px] text-muted-foreground italic py-4 text-center">
                    ✓ No open advances. Everyone has fully accounted for their funds.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {data.pending_advances.map((a, idx) => (
                      <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 border border-border/40" data-testid={`inbox-advance-${idx}`}>
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300 border">{a.person_type}</Badge>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold truncate">{a.person_name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {a.date?.slice(0, 10)} · gave {sym(a.amount)} · used {sym(a.consumed)}
                            {a.age_days > 60 && <span className="text-rose-700 ml-2"><AlertTriangle size={10} className="inline" /> {a.age_days}d old</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] text-muted-foreground">Remaining</div>
                          <div className="font-bold text-amber-700">{sym(a.remaining)}</div>
                        </div>
                        <Link to="/pass-through-advances" className="text-[11px] text-indigo-600 hover:underline flex items-center gap-0.5">
                          Settle <ChevronRight size={11} />
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* iter623 — AI Inbox-Clearing assistant. */}
            <Card className="border-violet-200 bg-gradient-to-br from-violet-50/50 to-indigo-50/30" data-testid="ai-clear-section">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="font-bold font-outfit text-base flex items-center gap-2">
                    <Sparkles size={16} className="text-violet-600" /> AI Inbox-Clearing
                  </h2>
                  <div className="flex flex-wrap gap-1.5">
                    {['week', 'month', 'quarter', 'year'].map(p => (
                      <Button
                        key={p}
                        size="sm" variant="outline"
                        onClick={() => runAiClearing(p)}
                        disabled={aiBusy === p}
                        className="border-violet-300 text-violet-700 hover:bg-violet-100 text-[11px] capitalize"
                        data-testid={`ai-clear-${p}`}
                      >
                        {aiBusy === p ? <Loader2 className="animate-spin mr-1" size={11} /> : <Sparkles size={11} className="mr-1" />}
                        Run {p === 'year' ? 'yearly' : p === 'quarter' ? 'quarterly' : `${p}ly`}
                      </Button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-violet-700/70">
                  AI reads every pending fee + open advance for the period and produces a concrete to-do list.
                  Click a frequency above to generate the plan, or subscribe below to get it emailed automatically.
                </p>
                <div className="flex flex-wrap gap-2 pt-1 border-t border-violet-200" data-testid="ai-clear-subs">
                  {['week', 'month', 'quarter', 'year'].map(p => (
                    <label key={p} className="flex items-center gap-1.5 text-[11px] text-violet-800 cursor-pointer">
                      <input type="checkbox"
                        checked={!!subs[p]?.enabled}
                        onChange={() => toggleSub(p)}
                        className="rounded"
                        data-testid={`ai-sub-${p}`} />
                      Email me {p === 'year' ? 'yearly' : p === 'quarter' ? 'quarterly' : `${p}ly`}
                    </label>
                  ))}
                </div>
                {aiResult && (
                  <div className="bg-white border border-violet-200 rounded-lg p-3 space-y-2" data-testid="ai-clear-result">
                    <div className="flex items-center justify-between flex-wrap gap-1">
                      <span className="text-[11px] uppercase font-bold text-violet-700 tracking-wider">
                        {aiResult.period} plan · {aiResult.fees_count || 0} fees · {aiResult.advances_count || 0} advances
                      </span>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" onClick={doAll} disabled={bulkBusy}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7" data-testid="ai-bulk-do-all">
                          {bulkBusy ? <Loader2 className="animate-spin mr-1" size={11} /> : '✓'} Do all suggestions
                        </Button>
                        <button type="button" onClick={() => setAiResult(null)} className="text-[11px] text-muted-foreground hover:text-foreground">Dismiss</button>
                      </div>
                    </div>
                    {/* iter624 — Executable suggestions: each row has a
                        "Do it" button that calls /recon-inbox/apply-suggestion. */}
                    {(aiResult.fee_suggestions || []).length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[10px] uppercase font-bold text-rose-700 tracking-wider">Fees to book</div>
                        {aiResult.fee_suggestions.map((s, idx) => (
                          <div key={`fee-${idx}`} className="flex items-center gap-2 p-1.5 rounded border border-rose-200 bg-rose-50/40 text-[11px]">
                            <span className="flex-1 truncate">{s.label}</span>
                            <button type="button" onClick={() => previewSuggestion(s)}
                              className="text-[10px] text-indigo-700 hover:underline"
                              data-testid={`ai-preview-fee-${idx}`}>Preview</button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] border-rose-300 text-rose-700 hover:bg-rose-100"
                              onClick={() => applySuggestion(s, idx, 'fee')}
                              disabled={appliedIds.has(`fee-${idx}`)}
                              data-testid={`ai-apply-fee-${idx}`}>
                              {appliedIds.has(`fee-${idx}`) ? '✓ Done' : 'Do it'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {(aiResult.advance_suggestions || []).length > 0 && (
                      <div className="space-y-1">
                        <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wider">Advances to chase</div>
                        {aiResult.advance_suggestions.map((s, idx) => (
                          <div key={`adv-${idx}`} className="flex items-center gap-2 p-1.5 rounded border border-amber-200 bg-amber-50/40 text-[11px]">
                            <span className="flex-1 truncate">{s.label}</span>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-100"
                              onClick={() => applySuggestion({ action: 'chase', advance_id: s.id }, idx, 'advance')}
                              disabled={appliedIds.has(`advance-${idx}`)}
                              data-testid={`ai-apply-advance-${idx}`}>
                              {appliedIds.has(`advance-${idx}`) ? '✓ Logged' : 'Chase'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <details className="text-[11px]">
                      <summary className="cursor-pointer text-violet-700 font-semibold">Full AI plan (markdown)</summary>
                      <pre className="whitespace-pre-wrap font-sans text-[12px] text-stone-800 leading-relaxed mt-1">{aiResult.raw_text}</pre>
                    </details>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* iter626 — Auto-book recurring fee rules (dry-run / 1-click). */}
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-teal-50/30" data-testid="recurring-rules-section">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="font-bold font-outfit text-base flex items-center gap-2">
                    <Repeat size={16} className="text-emerald-600" /> Auto-book recurring fees
                  </h2>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline"
                      onClick={async () => {
                        try {
                          const { data } = await api.post('/recon-inbox/recurring-rules/auto-book-now');
                          if (data.posted > 0) toast.success(`Auto-booked ${data.posted} fee(s) across ${data.scanned_rules} rule(s)`);
                          else toast.info(`No new fees to book (scanned ${data.scanned_rules} rule(s)).`);
                          await loadRecurring();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Auto-book failed');
                        }
                      }}
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-[11px] h-7"
                      data-testid="rr-autobook-now-btn"
                      title="Run the auto-book sweep right now without waiting for the daily 04:00 UTC scheduler. Idempotent — safe to run anytime.">
                      <Play size={11} className="mr-1" /> Run auto-book now
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={scanRecurring} disabled={rrBusy}
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-[11px] h-7"
                      data-testid="rr-scan-btn">
                      {rrBusy ? <Loader2 className="animate-spin mr-1" size={11} /> : <Sparkles size={11} className="mr-1" />}
                      Scan last 3 months
                    </Button>
                  </div>
                </div>
                <p className="text-[11px] text-emerald-700/70">
                  Spot fees that recur every month within ±10% (e.g. SR 17.50 bank charges). Activate a rule, flip <strong>Auto-book</strong>, and the daily scheduler posts the journal entry the moment the next monthly fee lands — fully audited. Leave Auto-book off and you get a one-click <strong>Apply rule</strong> button instead.
                </p>

                {/* Active rules row */}
                {rrules.rules.length > 0 && (
                  <div className="space-y-1" data-testid="rr-active-rules">
                    <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Active rules ({rrules.rules.length})</div>
                    {rrules.rules.map(r => (
                      <div key={r.id} className="flex items-center gap-2 p-1.5 rounded border border-emerald-200 bg-white text-[11px]" data-testid={`rr-rule-${r.category}`}>
                        <Badge className={`${r.status === 'active' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-stone-100 text-stone-600 border-stone-300'} border capitalize`}>{r.status}</Badge>
                        <span className="flex-1 truncate">
                          {r.category.replace(/_/g, ' ')} · mean {sym(r.mean_amount)} · ±{r.tolerance_pct}%
                          {r.last_auto_booked_at && (
                            <span className="text-[9px] text-muted-foreground ml-1">· auto-booked {r.last_auto_booked_period}</span>
                          )}
                        </span>
                        <label
                          className="flex items-center gap-1 text-[10px] text-emerald-800 cursor-pointer select-none"
                          title="Post the journal entry automatically the next time this rule matches a pending bank-fee period (runs daily at 04:00 UTC). Idempotent — same period won't be booked twice."
                          data-testid={`rr-autobook-toggle-${r.category}`}
                        >
                          <input
                            type="checkbox"
                            className="h-3 w-3 cursor-pointer accent-emerald-600"
                            checked={!!r.auto_book}
                            disabled={r.status !== 'active'}
                            onChange={async (e) => {
                              try {
                                await api.patch(`/recon-inbox/recurring-rules/${r.id}`, { auto_book: e.target.checked });
                                toast.success(e.target.checked ? 'Auto-book ON for this rule' : 'Auto-book OFF');
                                await loadRecurring();
                              } catch (err) {
                                toast.error('Failed to update auto-book');
                              }
                            }}
                          />
                          Auto-book
                        </label>
                        {r.status === 'active' ? (
                          <Button size="sm" variant="ghost" className="h-6 text-[10px] text-amber-700" onClick={() => updateRuleStatus(r, 'paused')} data-testid={`rr-pause-${r.category}`}>
                            <Pause size={10} className="mr-0.5" /> Pause
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-6 text-[10px] text-emerald-700" onClick={() => updateRuleStatus(r, 'active')} data-testid={`rr-resume-${r.category}`}>
                            <Play size={10} className="mr-0.5" /> Resume
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 text-[10px] text-rose-700" onClick={() => updateRuleStatus(r, 'dismissed')} data-testid={`rr-dismiss-${r.category}`}>
                          <X size={10} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Matches awaiting 1-click apply */}
                {rrules.matches.length > 0 && (
                  <div className="space-y-1" data-testid="rr-matches">
                    <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wider">Matches awaiting your one-click ({rrules.matches.length})</div>
                    {rrules.matches.map(m => (
                      <div key={`${m.rule_id}-${m.period}`} className="flex items-center gap-2 p-1.5 rounded border border-amber-300 bg-amber-50/60 text-[11px]" data-testid={`rr-match-${m.category}-${m.period}`}>
                        <span className="flex-1 truncate">
                          <strong>{m.category.replace(/_/g, ' ')}</strong> · {m.period} · {sym(m.amount)}
                          <span className="text-[10px] text-muted-foreground ml-1">(rule mean {sym(m.rule_mean)} · {m.deviation_pct}% off · {m.age_days}d old)</span>
                        </span>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-6 text-[10px]" onClick={() => applyRuleMatch(m)} data-testid={`rr-apply-${m.category}-${m.period}`}>
                          Apply rule
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Scan candidates */}
                {rrules.candidates.length > 0 && (
                  <div className="space-y-1 border-t border-emerald-200 pt-2" data-testid="rr-candidates">
                    <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Recurring patterns we noticed ({rrules.candidates.length})</div>
                    {rrules.candidates.map(c => (
                      <div key={c.category} className={`flex items-center gap-2 p-1.5 rounded border text-[11px] ${c.already_active ? 'border-stone-200 bg-stone-50 opacity-60' : 'border-emerald-200 bg-white'}`} data-testid={`rr-candidate-${c.category}`}>
                        <span className="flex-1 truncate">
                          <strong>{c.category.replace(/_/g, ' ')}</strong> · ~{sym(c.mean_amount)} · seen in {c.periods_seen.length} month(s)
                          <span className="text-[10px] text-muted-foreground ml-1">(±{c.tolerance_pct}%)</span>
                        </span>
                        {c.already_active ? (
                          <span className="text-[10px] text-stone-500 italic">already active</span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-6 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-100" onClick={() => activateRule(c)} data-testid={`rr-activate-${c.category}`}>
                            Activate rule
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {rrules.rules.length === 0 && rrules.matches.length === 0 && rrules.candidates.length === 0 && (
                  <div className="text-center text-[12px] text-emerald-700/60 italic py-2">
                    Click <strong>Scan last 3 months</strong> to discover recurring fee patterns.
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}

        <div className="bg-info/5 border border-info/20 rounded-xl p-3 text-[11px] text-info/90">
          <strong>Why this inbox?</strong> Two pain points fixed: bank fees that sit on statements
          but never become expenses (audit gap), and money you&apos;ve given to staff that nobody
          ever accounted for (cash leak). Clear both here without hopping between five pages.
        </div>

        {/* iter625 — Dry-run preview dialog */}
        {dryRun && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDryRun(null)} data-testid="dry-run-modal">
            <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <h3 className="font-bold font-outfit text-base mb-2">Trial-balance impact preview</h3>
              <p className="text-[12px] text-muted-foreground mb-3">{dryRun.summary}</p>
              <div className="space-y-1">
                {(dryRun.changes || []).map((c, i) => (
                  <div key={i} className="flex justify-between text-[12px] p-2 rounded bg-stone-50">
                    <span>{c.account} <span className="text-[10px] text-muted-foreground">({c.type})</span></span>
                    <span className={c.delta >= 0 ? 'text-emerald-700 font-bold' : 'text-rose-700 font-bold'}>
                      {c.side} {Math.abs(c.delta).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => setDryRun(null)} size="sm">Close</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => { applySuggestion(dryRun.suggestion, -1, 'fee'); setDryRun(null); }}
                  data-testid="dry-run-confirm">
                  Looks good — apply
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
